import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { usersApi } from "@Api/users";
import { useKeycloak } from "@Contexts/KeycloakContext";
import { AuthorizedUserInfo } from "@Types/users";

interface UserAuthorizationContextData {
  permissions: string[];
  userAuthorization: AuthorizedUserInfo | null;
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (codes: string[]) => boolean;
  isLoading: boolean;
}

const UserAuthorizationContext = createContext<
  UserAuthorizationContextData | undefined
>(undefined);

export const useUserAuthorization = (): UserAuthorizationContextData => {
  const context = useContext(UserAuthorizationContext);
  if (!context) {
    throw new Error(
      "useUserAuthorization must be used within UserAuthorizationProvider"
    );
  }
  return context;
};

export const usePermissions = useUserAuthorization;

interface UserAuthorizationProviderProps {
  children: ReactNode;
}

export const UserAuthorizationProvider: React.FC<
  UserAuthorizationProviderProps
> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [userAuthorization, setUserAuthorization] =
    useState<AuthorizedUserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { isAuthenticated, isLoading: isAuthLoading } = useKeycloak();

  useEffect(() => {
    if (!isAuthLoading && isAuthenticated) {
      usersApi
        .getAuthorizedUser()
        .then((data) => {
          setUserAuthorization(data);
          setPermissions(data.permissionCodes ?? []);
        })
        .catch(() => {
          setPermissions([]);
          setUserAuthorization(null);
        })
        .finally(() => setIsLoading(false));
    } else if (!isAuthLoading && !isAuthenticated) {
      setPermissions([]);
      setUserAuthorization(null);
      setIsLoading(false);
    }
  }, [isAuthenticated, isAuthLoading]);

  const hasPermission = useCallback(
    (code: string): boolean => permissions.includes(code),
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (codes: string[]): boolean =>
      codes.some((code) => permissions.includes(code)),
    [permissions]
  );

  return (
    <UserAuthorizationContext.Provider
      value={{
        permissions,
        userAuthorization,
        hasPermission,
        hasAnyPermission,
        isLoading,
      }}
    >
      {children}
    </UserAuthorizationContext.Provider>
  );
};
